/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, Suspense, useState } from 'react';
import { Await } from 'react-router';
import type { ShopperSearch } from '@/scapi';
import { useDeferredRender } from '@/hooks/use-deferred-render';
import ProductList from '@/components/product-list';
import type { ProductListingView } from '@/lib/product/view-preference';
import ProductGrid from './grid';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

/**
 * Wraps ProductGrid with three-phase deferred rendering for non-critical products.
 *
 * Why: Mounting a Suspense boundary on initial render forces React to retain the entire fallback
 * tree in memory and blocks the main thread while it reconciles the pending subtree — even if the
 * user hasn't scrolled to that content yet. This hurts LCP and inflates TBT. By deferring the
 * Suspense mount until after an idle callback, the critical (above-the-fold) tiles paint without
 * contention, then the non-critical tiles stream in once the browser is idle.
 *
 * Additionally, extracting this wrapper from ProductGrid itself keeps error handling explicit and
 * consistent across all routes: the error fallback always preserves critical tiles, rather than
 * each route reimplementing its own Suspense/Await/error pattern.
 *
 * Phase 1 (Pre-Idle): Show critical tiles + lightweight skeletons without mounting the Suspense
 * boundary. This keeps the initial render minimal, reducing TBT and improving LCP.
 *
 * Phase 2 (Post-Idle, Pending): Idle callback fires, Suspense boundary mounts with skeleton fallback.
 *
 * Phase 3 (Resolved): Non-critical products rendered via ProductGrid.
 */
export default function DeferredProductGrid({
    critical,
    nonCritical,
    nonCriticalCount = 0,
    hasRefinementsPanel,
    panelWidths,
    suppressEmptyMessage,
    handleProductClick,
    isLoading,
    errorElement,
    view = 'grid',
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable,
}: {
    critical?: ProductSearchHit[];
    nonCritical: Promise<ProductSearchHit[]>;
    nonCriticalCount?: number;
    hasRefinementsPanel?: boolean;
    /** Forwarded to `ProductGrid`. Set by a host whose panel is not the shared set's 264px. */
    panelWidths?: string[];
    /** Forwarded to `ProductGrid`. Set by a host rendering its own empty-result message. */
    suppressEmptyMessage?: boolean;
    handleProductClick?: (product: ProductSearchHit) => void;
    isLoading?: boolean;
    errorElement?: ReactElement;
    /**
     * Which presentation to render. Defaults to `grid`, so existing callers — the search route
     * and any consumer that does not offer the choice — are unaffected.
     */
    view?: ProductListingView;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}): ReactElement {
    const shouldRenderNonCritical = useDeferredRender(nonCriticalCount > 0);

    // Pin the non-critical promise to its first reference for the lifetime of this component
    // instance. Without pinning, any non-navigating loader revalidation (e.g. a useFetcher.submit
    // from a tile-level mutation like wishlist-add) returns a fresh promise from the loader, which
    // <Await> treats as a new resolve and re-suspends — unmounting every tile (and any in-flight
    // fetcher whose owning component lives inside). Routes invalidate this pinned reference by
    // remounting via `key={productGridDataKey}` whenever URL identity changes (pagination, sort,
    // refine), which is the only time we actually want fresh non-critical data.
    const [pinnedNonCritical] = useState(() => nonCritical);

    const gridProps = {
        critical,
        hasRefinementsPanel,
        panelWidths,
        suppressEmptyMessage,
        handleProductClick,
        isLoading,
        // @sfdc-extension-line SFDC_EXT_BOPIS
        showPickupAvailable,
    };

    /**
     * Renders whichever presentation is selected, so the three phases below stay a single copy.
     *
     * The branch is here rather than in the route deliberately: the phase logic, the promise
     * pinning above and the error path that preserves critical results are not grid-specific,
     * and a second deferred wrapper for the table would be a second copy of all three, free to
     * drift. Grid-only props (image widths, top category, pickup) are simply not passed to the
     * list — it renders a fixed-width thumbnail and needs none of them.
     */
    const presentation = (resolved?: ProductSearchHit[], skeletonCount?: number) =>
        view === 'list' ? (
            <ProductList
                critical={critical}
                nonCritical={resolved}
                handleProductClick={handleProductClick}
                isLoading={isLoading}
                skeletonCount={skeletonCount}
            />
        ) : (
            <ProductGrid {...gridProps} nonCritical={resolved} skeletonCount={skeletonCount} />
        );

    // Phase 1: Pre-idle — show critical results + skeleton placeholders
    if (!shouldRenderNonCritical) {
        return presentation(undefined, nonCriticalCount);
    }

    // Phase 2 & 3: Post-idle — mount Suspense + Await, resolve non-critical results.
    // When the promise rejects, render critical results alongside the errorElement so
    // above-the-fold products remain visible even when non-critical loading fails.
    const errorFallback = errorElement ? (
        <>
            {presentation()}
            {errorElement}
        </>
    ) : undefined;

    return (
        <Suspense fallback={presentation(undefined, nonCriticalCount)}>
            <Await resolve={pinnedNonCritical} errorElement={errorFallback}>
                {(products: ProductSearchHit[]) => presentation(products)}
            </Await>
        </Suspense>
    );
}
